import { useState, useRef, useEffect } from 'react';
import './App.css';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import CanvasPrintAndFraming from './Components/CanvasPrintAndFraming';
import PrintandFrameMabtoardandGlass from './Components/PrintandFramewithMatboardandGlass';
import Header from './Components/Header';
import FrameYourCanvasArt from './Components/FrameYourCanvasArt';
import FrameYourPaperArt from './Components/FrameYourPaperArt';
import MatOnly from './Components/MatOnly';
import Swal from 'sweetalert2';

function App() {
  const [step, setStep] = useState('upload'); // upload | edit | result
  const [imageSrc, setImageSrc] = useState(() => {
    return localStorage.getItem('opticon_last_image') || null;
  });
  const [uploadedImage, setUploadedImage] = useState(() => {
    return localStorage.getItem('opticon_uploaded_image') || null;
  });
  // Crop State
  const [crop, setCrop] = useState({ unit: '%', width: 50, aspect: 16 / 9 }); // Default crop
  const [completedCrop, setCompletedCrop] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [userHasCropped, setUserHasCropped] = useState(false);
  const [cropAspect, setCropAspect] = useState(null);
  // Refs and Dragging
  const [isDragging, setIsDragging] = useState(false);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const isProductScreen = selectedProduct !== null;
  // Detect hard refresh
  const navEntries = performance.getEntriesByType?.('navigation') || [];
  const wasRefreshed = navEntries.length > 0 && navEntries[0].type === 'reload';
  const [returnProduct, setReturnProduct] = useState(null);
  useEffect(() => {
    if (wasRefreshed) {
      localStorage.removeItem('opticon_last_image');
      localStorage.removeItem('opticon_uploaded_image');
    }
  }, []); // run once only

  // ---------------- FLOW DETECTION ----------------
  const currentPath = window.location.pathname
    .replace(/\/$/, '')
    .toLowerCase();

  // Supported flows.
  const FLOW_PRINT_AND_FRAME = 'printandframe';
  const FLOW_JUST_FRAMES = 'justframes';

  // Detect the active flow from the final URL segment.
  const routeSegment = currentPath.split('/').filter(Boolean).pop() || '';
  const activeFlow = routeSegment === 'justframes'
    ? FLOW_JUST_FRAMES
    : FLOW_PRINT_AND_FRAME;

  const justFramesCards = [
    {
      label: 'Frame Your Canvas Art',
      key: 'frame-your-canvas-art',
      img: '/assets/frames/frame-your-canvas-art.jfif'
    },
    {
      label: 'Frame your paper art',
      key: 'frame-your-paper-art',
      img: '/assets/frames/frame-your-paper-art.jfif'
    },
    {
      label: 'Mat only',
      key: 'mat-only',
      img: '/assets/frames/mat-only.jfif'
    }
  ];

  /* ---------------- Upload Logic ---------------- */
  const readFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;

    const MAX_MB = 64;

    if (file.size > MAX_MB * 1024 * 1024) {
      Swal.fire({
        icon: 'error',
        title: 'Image too large',
        text: 'Please upload an image under 64MB.',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Reset crop state
    setUserHasCropped(false);
    setCroppedImage(null);
    setCompletedCrop(null);

    // Create object URL for image
    const url = URL.createObjectURL(file);
    const reader = new FileReader();
    reader.onload = () => {
      setUploadedImage(reader.result);
      localStorage.setItem('opticon_uploaded_image', reader.result);
    };
    reader.readAsDataURL(file);
    setImageSrc(url);
    setStep('edit');
  };

  useEffect(() => {
    return () => {
      if (imageSrc) URL.revokeObjectURL(imageSrc);
    };
  }, [imageSrc]);

  const handleFileSelect = (e) => {
    if (e.target.files?.length) readFile(e.target.files[0]);
  };

  const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      readFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  /* ---------------- Crop Export Logic ---------------- */
  // Initialize crop when image loads
  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    let cropWidth = 90;
    let cropHeight = 90;

    if (cropAspect) {
      cropHeight = (cropWidth * width) / (cropAspect * height);

      if (cropHeight > 90) {
        cropHeight = 90;
        cropWidth = (cropHeight * cropAspect * height) / width;
      }
    }

    const centerCrop = {
      unit: '%',
      width: cropWidth,
      height: cropHeight,
      x: (100 - cropWidth) / 2,
      y: (100 - cropHeight) / 2
    };
    setCrop(centerCrop);

  };

  const openCropEditor = (productKey, options = {}) => {
    const requestedAspect = Number(options.aspect);

    setCropAspect(
      Number.isFinite(requestedAspect) && requestedAspect > 0
        ? requestedAspect
        : null
    );
    setReturnProduct(productKey || null);
    setCompletedCrop(null);
    setUserHasCropped(false);
    setStep('edit');
  };

  const handleCancelCrop = () => {
    if (returnProduct) {
      setSelectedProduct(returnProduct);
      setReturnProduct(null);
      setStep('result');
      return;
    }

    setStep('upload');
  };

  const getPixelCrop = (sourceCrop, image) => {
    if (!sourceCrop || !image) return null;

    if (sourceCrop.unit === '%') {
      return {
        x: (Number(sourceCrop.x) || 0) * image.width / 100,
        y: (Number(sourceCrop.y) || 0) * image.height / 100,
        width: (Number(sourceCrop.width) || 0) * image.width / 100,
        height: (Number(sourceCrop.height) || 0) * image.height / 100
      };
    }

    return sourceCrop;
  };

  const handleProcessImage = async () => {
    const image = imgRef.current;
    const cropToUse = completedCrop || getPixelCrop(crop, image);

    // User never edited and no requested aspect - use original.
    if (!(userHasCropped || cropAspect) || !cropToUse || !image) {
      setCroppedImage(imageSrc);
      localStorage.setItem('opticon_last_image', imageSrc);
      setStep('result');
      return;
    }

    const activeCrop = cropToUse;

    if (activeCrop.width === 0 || activeCrop.height === 0) {
      setCroppedImage(imageSrc);
      localStorage.setItem('opticon_last_image', imageSrc);
      setStep('result');
      return;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const canvas = document.createElement('canvas');
    canvas.width = activeCrop.width * scaleX;
    canvas.height = activeCrop.height * scaleY;

    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      activeCrop.x * scaleX,
      activeCrop.y * scaleY,
      activeCrop.width * scaleX,
      activeCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.95)
    );

    const url = URL.createObjectURL(blob);
    setCroppedImage(url);
    localStorage.setItem('opticon_last_image', url);

    if (returnProduct) {
      setSelectedProduct(returnProduct);
      setReturnProduct(null);
      setStep('result');
    } else {
      setStep('result');
    }

  };

  /* ---------------- Effects ---------------- */
  useEffect(() => {
    if (!imageSrc) {
      setStep('upload');
      setSelectedProduct(null);
    }
  }, [imageSrc]);

  useEffect(() => {
    if (step === 'edit') {
      // Reset crop logic if needed when entering edit mode
      // But we generally wait for onLoad
    }
  }, [step]);

  /* ---------------- UI ---------------- */
  return (
    <div className="App">
      <Header showBack={!!selectedProduct} onBack={() => setSelectedProduct(null)} />

      {/* UI of /printAndFrame Starts*/}
      {/* Upload Modal */}
      {step === 'upload' && activeFlow === FLOW_PRINT_AND_FRAME && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="modal-title">Image of the subject</h2>
            <p className="modal-subtitle">Upload image</p>
            <div
              className={`upload-area ${isDragging ? 'dragging' : ''}`}
              onClick={() => fileInputRef.current.click()}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              <p>{isDragging ? 'Drop image here' : 'Drag & drop or click to upload'}</p>
              <button className="btn-primary">Select</button>
              <input type="file" ref={fileInputRef} className="file-input" accept="image/*" onChange={handleFileSelect} />
            </div>
          </div>
        </div>
      )}

      {/* Edit/Crop Modal */}
      {step === 'edit' && activeFlow === FLOW_PRINT_AND_FRAME && (
        <div className="modal-overlay">
          <div className="edit-modal modal-content">
            <h2 className="modal-title">Adjustment of the image</h2>
            <p className="modal-subtitle">Select the area you want to print</p>

            <div className="crop-container-wrapper">
              {/* ReactCrop MUST wrap the image tag */}
              <ReactCrop
                crop={crop}
                aspect={cropAspect || undefined}
                onChange={(pixelCrop, percentCrop) => {
                  setCrop(percentCrop || pixelCrop);
                  setUserHasCropped(true);   // ✅ user touched crop
                }}
                onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
              // Remove aspect if you want free-form cropping
              // aspect={16 / 9} 
              >
                <img
                  ref={imgRef}
                  src={imageSrc}
                  alt="Crop me"
                  onLoad={onImageLoad}
                  style={{ maxWidth: '100%', maxHeight: '60vh' }}
                />
              </ReactCrop>
            </div>

            <div className="action-buttons">
              <button className="btn-success" onClick={handleProcessImage}>
                Proceed
              </button>

              <button className="btn-warning" onClick={handleCancelCrop}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Selection */}
      {step === 'result' && !isProductScreen && activeFlow === FLOW_PRINT_AND_FRAME && (
        <div className="main-content">
          <h1 className="section-title">Please select the type of frame you want to create:</h1>
          <div className="cards-grid">
            {[
              /*
              { label: 'Print Only', key: 'print-only' },
              { label: 'Canvas Print & Stretch', key: 'canvas-stretch' },
              { label: 'Canvas Print & Frame', key: 'canvas-frame' },
              */
              { label: 'Canvas Print & Framing', key: 'canvas-print-framing' },
              { label: 'Print & Frame with Matboard', key: 'print-frame-matboard' }
            ].map((item, idx) => (
              <div
                className="card"
                key={idx}
                onClick={() => {
                  // Ensure we use the cropped version
                  localStorage.setItem('opticon_last_image', croppedImage);
                  setSelectedProduct(item.key);
                }}
              >
                <div className="card-image-wrapper">
                  {item.key === 'print-frame-matboard' ? (
                    <img
                      src={croppedImage || imageSrc}
                      className="card-img print-frame-matboard"
                      alt="result"

                    />
                  ) : (
                    <img
                      src={croppedImage || imageSrc}
                      className={`card-img ${item.key === 'canvas-print-framing' || item.key === 'canvas-frame'
                        ? 'card-img-bordered'
                        : ''
                      }`}
                      alt="result"
                    />
                  )}
                </div>
                <div className="card-footer">
                  <button className="card-btn">{item.label}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Product Component Rendering */}

      {selectedProduct === 'canvas-print-framing' && (
        <CanvasPrintAndFraming image={croppedImage} originalImage={uploadedImage} onBack={() => setSelectedProduct(null)} onEditImage={(options) => { openCropEditor(selectedProduct, options); }} />
      )}

      {selectedProduct === 'print-frame-matboard' && (
        <PrintandFrameMabtoardandGlass image={croppedImage} originalImage={uploadedImage} onBack={() => setSelectedProduct(null)} onEditImage={(options) => { openCropEditor(selectedProduct, options); }} />
      )}
      {/* UI of /printAndFrame Ends*/}

      {/* UI of /justFrames Starts */}
      {activeFlow === FLOW_JUST_FRAMES && !isProductScreen && (
        <div className="main-content">
          <h1 className="section-title">
            Frame Your Existing Art or Photo
          </h1>

          <p className="section-subtitle">
            Please select the type of frame you want to create for your existing art:
          </p>

          <div className="cards-grid three-cols">
            {justFramesCards.map((item, idx) => (
              <div
                className="card"
                key={idx}
                onClick={() => setSelectedProduct(item.key)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-image-wrapper">
                  <img
                    src={item.img}
                    alt={item.label}
                    className="card-image"
                  />
                </div>

                <div className="card-footer">
                  <button className="card-btn">
                    {item.label}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedProduct === 'frame-your-canvas-art' && (
        <FrameYourCanvasArt image={croppedImage} originalImage={uploadedImage} onBack={() => setSelectedProduct(null)} />
      )}

      {selectedProduct === 'frame-your-paper-art' && (
        <FrameYourPaperArt image={croppedImage} originalImage={uploadedImage} onBack={() => setSelectedProduct(null)} />
      )}

      {selectedProduct === 'mat-only' && (
        <MatOnly image={croppedImage} originalImage={uploadedImage} onBack={() => setSelectedProduct(null)} />
      )}

      {/* UI of /justFrames Ends */}

    </div>
  );
}

export default App;
